import React, { useContext, useEffect, useState } from "react";
import { useTheme, Grid, Typography, Button } from "@mui/material";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { Link, useParams } from "react-router-dom";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import FieldSection, { Field } from "../../components/general/FieldSection";
import useCompanyDetails from "../../hooks/companies/useCompanyDetails";
import { UpdateCompanyDto } from "../../types/companyTypes";
import useIndustries from "../../hooks/companies/useIndustries";

/**
 * Page for editing an existing company
 */
const EditCompany: React.FunctionComponent = () => {
  const theme = useTheme();

  /**
   * Function which provides the styles of the EditCompany
   */
  const styles = {
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
  };

  const { id } = useParams();
  const companyId = Number(id);
  const { companyDetails, updateCompany } = useCompanyDetails(companyId);

  const { auth } = useContext(AuthContext);
  const hasCrmPermissions = doesPermissionsHaveSomeOf(auth.permissions, [13]);

  // Check if the user is PL of at least one project of the company
  const isPLOfSomeProjects = companyDetails?.projects?.some((project) =>
    project.projectMembers.some((member) => member.memberId === auth.userID)
  );

  const { allIndustries } = useIndustries();

  const [editedCompany, setEditedCompany] = useState<UpdateCompanyDto>({
    companyId,
    name: companyDetails?.name || "",
    industry: companyDetails?.industry || { industryId: -1, description: "" },
    shortDescription: companyDetails?.shortDescription || "",
    addressAdditional: companyDetails?.addressAdditional || "",
    street: companyDetails?.street || "",
    postalCode: companyDetails?.postalCode || "",
    city: companyDetails?.city || "",
    url: companyDetails?.url || "",
    importantInformation: companyDetails?.importantInformation || "",
    contactDesired: companyDetails?.contactDesired || false,
    classified: companyDetails?.classified || false,
  });

  useEffect(() => {
    if (companyDetails) {
      setEditedCompany({
        companyId,
        name: companyDetails.name,
        industry: companyDetails.industry,
        shortDescription: companyDetails.shortDescription || "",
        addressAdditional: companyDetails.addressAdditional || "",
        street: companyDetails.street || "",
        postalCode: companyDetails.postalCode || "",
        city: companyDetails.city || "",
        url: companyDetails.url || "",
        importantInformation: companyDetails.importantInformation || "",
        contactDesired: companyDetails.contactDesired || false,
        classified: companyDetails.classified || false,
      });
    }
  }, [companyDetails, companyId]);

  const [isNameFieldEmpty, setIsNameFieldEmpty] = useState<boolean>(false);
  const [isIndustryFieldEmpty, setIsIndustryFieldEmpty] = useState<boolean>(false);
  const [isPostalCodeFieldInvalid, setIsPostalCodeFieldInvalid] = useState<boolean>(false);

  /**
   * Handles the industry selection
   */
  const handleIndustryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const industryId = Number(event.target.value);
    const selectedIndustry = allIndustries.find((industry) => industry.industryId === industryId);
    if (selectedIndustry !== undefined) {
      setEditedCompany((prev) => ({ ...prev, industry: selectedIndustry }));
    }
  };

  /**
   * Submits the inputted company data if critical fields are not empty.
   */
  const handleSubmit = async () => {
    if (
      editedCompany.name.trim() === "" ||
      editedCompany.industry.industryId === -1 ||
      (editedCompany.postalCode.trim() !== "" && editedCompany.postalCode.length > 10)
    ) {
      setIsNameFieldEmpty(editedCompany.name.trim() === "");
      setIsIndustryFieldEmpty(editedCompany.industry.industryId === -1);
      setIsPostalCodeFieldInvalid(editedCompany.postalCode.trim() !== "" && editedCompany.postalCode.length > 10);
      return;
    }

    setIsNameFieldEmpty(false);
    setIsIndustryFieldEmpty(false);
    setIsPostalCodeFieldInvalid(false);

    await updateCompany(editedCompany);
  };

  const fields: Array<Field> = [
    {
      label: "Name",
      state: editedCompany.name,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, name: e.target.value })),
      type: "Text",
      helperText: isNameFieldEmpty ? "Bitte gib einen Namen ein!" : undefined,
      error: isNameFieldEmpty,
    },
    {
      label: "Branche",
      state: editedCompany.industry.industryId,
      width: "full",
      onChangeCallback: handleIndustryChange,
      type: "Dropdown",
      values: allIndustries.map((industry) => ({
        value: industry.industryId,
        label: industry.description,
      })),
      helperText: isIndustryFieldEmpty ? "Bitte gib eine Branche ein!" : undefined,
      error: isIndustryFieldEmpty,
    },
    {
      label: "Kurzbeschreibung",
      state: editedCompany.shortDescription,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, shortDescription: e.target.value })),
      type: "TextBig",
      rows: 4,
    },
    {
      label: "Adresszusatz",
      state: editedCompany.addressAdditional,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, addressAdditional: e.target.value })),
      type: "Text",
    },
    {
      label: "Straße/Nr",
      state: editedCompany.street,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, street: e.target.value })),
      type: "Text",
    },
    {
      label: "PLZ",
      state: editedCompany.postalCode,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, postalCode: e.target.value })),
      type: "Text",
      helperText: isPostalCodeFieldInvalid ? "Bitte gib eine gültige Postleitzahl ein!" : undefined,
      error: isPostalCodeFieldInvalid,
    },
    {
      label: "Ort",
      state: editedCompany.city,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, city: e.target.value })),
      type: "Text",
    },
    {
      label: "Webseite",
      state: editedCompany.url,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, url: e.target.value })),
      type: "Text",
    },
    {
      label: "Wichtige Informationen",
      state: editedCompany.importantInformation,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, importantInformation: e.target.value })),
      type: "TextBig",
      rows: 4,
    },
    {
      label: "Kontakt erwünscht",
      state: editedCompany.contactDesired,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, contactDesired: e.target.checked })),
      type: "Checkbox",
    },
    {
      label: "Geheim",
      state: editedCompany.classified,
      width: "full",
      onChangeCallback: (e) => setEditedCompany((prev) => ({ ...prev, classified: e.target.checked })),
      type: "Checkbox",
    },
  ];

  return (
    <div>
      {(hasCrmPermissions || isPLOfSomeProjects) && (
        <Grid container spacing={1} sx={{ paddingTop: "30px", paddingLeft: "10px" }}>
          <Grid item>
            <Button sx={styles.button} variant="contained" component={Link} to={`/unternehmen/${id}`}>
              Abbrechen
            </Button>
          </Grid>
          <Grid item>
            <FieldSection title={"Kunden bearbeiten"} fields={fields}></FieldSection>
          </Grid>
          <Grid item xs={12} sx={{ paddingLeft: theme.spacing(2), paddingTop: theme.spacing(1) }}>
            <Typography variant="h5" sx={{ marginBottom: theme.spacing(1) }}>
              Ansprechpersonen
            </Typography>
            {companyDetails?.contactPersons && companyDetails.contactPersons.length > 0 ? (
              companyDetails.contactPersons.map((cp) => (
                <Typography key={cp.contactPersonId} sx={{ marginBottom: "4px" }}>
                  <Link
                    to={`/ansprechpersonen/${cp.contactPersonId}`}
                    style={{ color: theme.palette.primary.main, textDecoration: "none" }}
                  >
                    {cp.name}
                  </Link>
                </Typography>
              ))
            ) : (
              <Typography color="text.secondary">Keine Ansprechpersonen vorhanden.</Typography>
            )}
            <Button
              sx={{ ...styles.button, marginTop: theme.spacing(1) }}
              variant="contained"
              component="a"
              href={`/ansprechpersonen/neu?companyId=${id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ansprechperson erstellen
            </Button>
          </Grid>
          <Grid item>
            <Button sx={styles.button} variant="contained" onClick={handleSubmit}>
              Speichern
            </Button>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default EditCompany;
